import React, { useEffect } from 'react';
import { Button, Card, Descriptions, Space, Table, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { usePayroll } from '../../hooks/usePayroll';
import PayrollStatusBadge from '../../components/PayrollStatusBadge';
import { PayrollEntry } from '../../types';

const { Text } = Typography;

const fmt = (n: number) => new Intl.NumberFormat('en-TZ', { minimumFractionDigits: 2 }).format(n);

const PayrollPeriodDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activePeriod, entries, isLoading, fetchPeriod, fetchEntries, approvePayroll, markAsPaid } = usePayroll();

  useEffect(() => {
    if (id) {
      fetchPeriod(Number(id));
      fetchEntries(Number(id));
    }
  }, [id]);

  const totalGross = entries.reduce((s, e) => s + e.grossPay, 0);
  const totalNet = entries.reduce((s, e) => s + e.netPay, 0);
  const totalDeductions = entries.reduce((s, e) => s + e.totalDeductions, 0);

  const columns = [
    { title: 'Employee', dataIndex: 'employeeName', key: 'employeeName' },
    { title: 'Emp No.', dataIndex: 'employeeNumber', key: 'employeeNumber', width: 100 },
    { title: 'Days Worked', dataIndex: 'daysWorked', key: 'daysWorked', width: 100 },
    { title: 'Gross Pay', key: 'gross', render: (_: any, r: PayrollEntry) => fmt(r.grossPay), align: 'right' as const },
    { title: 'Deductions', key: 'ded', render: (_: any, r: PayrollEntry) => fmt(r.totalDeductions), align: 'right' as const },
    {
      title: 'Net Pay', key: 'net',
      render: (_: any, r: PayrollEntry) => <Text strong style={{ color: '#52c41a' }}>{fmt(r.netPay)}</Text>,
      align: 'right' as const,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: PayrollEntry) => (
        <Button size="small" onClick={() => navigate(`/hr/payroll/${id}/payslips/${r.id}`)}>Payslip</Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/payroll')} style={{ marginBottom: 16 }}>
        Back to Payroll
      </Button>

      {activePeriod && (
        <Card
          title={activePeriod.periodName}
          extra={
            <Space>
              <PayrollStatusBadge status={activePeriod.status} />
              {activePeriod.status === 'PENDING_APPROVAL' && (
                <Button type="primary" onClick={() => approvePayroll(activePeriod.id)} loading={isLoading}>
                  Approve &amp; Post GL
                </Button>
              )}
              {activePeriod.status === 'APPROVED' && (
                <Button onClick={() => markAsPaid(activePeriod.id)} loading={isLoading}>Mark as Paid</Button>
              )}
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={3} size="small">
            <Descriptions.Item label="Period">{activePeriod.startDate} – {activePeriod.endDate}</Descriptions.Item>
            <Descriptions.Item label="Pay Date">{activePeriod.payDate ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Employees">{activePeriod.processedEmployees}/{activePeriod.totalEmployees}</Descriptions.Item>
            <Descriptions.Item label="Total Gross">TZS {fmt(totalGross)}</Descriptions.Item>
            <Descriptions.Item label="Total Deductions">TZS {fmt(totalDeductions)}</Descriptions.Item>
            <Descriptions.Item label="Total Net Pay"><Text strong style={{ color: '#52c41a' }}>TZS {fmt(totalNet)}</Text></Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title="Payroll Entries">
        <Table dataSource={entries} columns={columns} rowKey="id" loading={isLoading} size="small" />
      </Card>
    </div>
  );
};

export default PayrollPeriodDetailView;
